/**
 * Lead attribution fields agreed with Nordiska Inglasningar (Christoffer Kun, 2026-09-27).
 *
 * "Latest" = most recent visit that carried a utm parameter or click id; sent empty
 * after 90 days. "First" = the very first visit, written once and never overwritten.
 * Field names are the exact lowercase keys the CRM (/formularsvar) expects.
 */

export const LATEST_TRACKED_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "landing_url",
] as const;

export const FIRST_VISIT_FIELDS = [
  "first_utm_source",
  "first_utm_medium",
  "first_utm_campaign",
  "first_landing_url",
  "first_seen_at",
] as const;

export const ATTRIBUTION_FIELDS = [
  ...LATEST_TRACKED_FIELDS,
  ...FIRST_VISIT_FIELDS,
] as const;

export type AttributionField = (typeof ATTRIBUTION_FIELDS)[number];
export type AttributionFields = Record<AttributionField, string>;

export const ATTRIBUTION_MAX_LENGTH: Record<AttributionField, number> = {
  utm_source: 100,
  utm_medium: 100,
  utm_campaign: 100,
  utm_content: 100,
  utm_term: 100,
  gclid: 255,
  gbraid: 255,
  wbraid: 255,
  fbclid: 255,
  landing_url: 500,
  first_utm_source: 100,
  first_utm_medium: 100,
  first_utm_campaign: 100,
  first_landing_url: 500,
  first_seen_at: 25,
};

export const LATEST_VISIT_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

type LatestKey = (typeof LATEST_TRACKED_FIELDS)[number];
const LATEST_SIGNAL_KEYS: LatestKey[] = LATEST_TRACKED_FIELDS.filter(
  key => key !== "landing_url"
);
const ISO_8601_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/;

/** What is kept between visits (localStorage) or in memory for the current tab. */
export interface AttributionState {
  latest?: Partial<Record<LatestKey, string>> & { seenAt: number };
  first?: {
    first_utm_source: string;
    first_utm_medium: string;
    first_utm_campaign: string;
    first_landing_url: string;
    first_seen_at: string;
  };
}

/** Empty when missing, "undefined"/"null" or longer than allowed — never truncated. */
export function cleanAttributionValue(
  field: AttributionField,
  value: unknown
): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return "";
  if (trimmed.length > ATTRIBUTION_MAX_LENGTH[field]) return "";
  if (field === "first_seen_at" && !ISO_8601_OFFSET.test(trimmed)) return "";
  return trimmed;
}

export function emptyAttribution(): AttributionFields {
  return Object.fromEntries(
    ATTRIBUTION_FIELDS.map(field => [field, ""])
  ) as AttributionFields;
}

/** Normalises untrusted input (client storage or request body) into all 15 keys. */
export function normalizeAttribution(input: unknown): AttributionFields {
  const source =
    input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const result = emptyAttribution();
  for (const field of ATTRIBUTION_FIELDS) {
    result[field] = cleanAttributionValue(field, source[field]);
  }
  return result;
}

/** Origin + path only: no query string or fragment ever leaves the browser. */
export function landingUrlFrom(href: string): string {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return "";
  }
}

/** ISO 8601 with the local UTC offset, e.g. 2026-09-28T14:05:00+02:00. */
export function toIsoWithOffset(date: Date): string {
  const pad = (n: number) => String(Math.abs(n)).padStart(2, "0");
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(Math.floor(Math.abs(offsetMinutes) / 60))}:${pad(Math.abs(offsetMinutes) % 60)}`
  );
}

/** Applies one page landing to the stored state according to the agreed rules. */
export function recordVisit(
  state: AttributionState,
  href: string,
  now: Date
): AttributionState {
  let params: URLSearchParams;
  try {
    params = new URL(href).searchParams;
  } catch {
    return state;
  }
  const landingUrl = cleanAttributionValue("landing_url", landingUrlFrom(href));
  const next: AttributionState = { ...state };

  const visit: Partial<Record<LatestKey, string>> = {};
  for (const key of LATEST_SIGNAL_KEYS) {
    visit[key] = cleanAttributionValue(key, params.get(key));
  }
  const hasTracking = LATEST_SIGNAL_KEYS.some(key => visit[key]);
  if (hasTracking) {
    next.latest = { ...visit, landing_url: landingUrl, seenAt: now.getTime() };
  }

  if (!state.first) {
    next.first = {
      first_utm_source: visit.utm_source ?? "",
      first_utm_medium: visit.utm_medium ?? "",
      first_utm_campaign: visit.utm_campaign ?? "",
      first_landing_url: cleanAttributionValue("first_landing_url", landingUrlFrom(href)),
      first_seen_at: toIsoWithOffset(now),
    };
  }
  return next;
}

/** The 15 fields to submit; latest-visit fields are blank once older than 90 days. */
export function attributionForSubmit(
  state: AttributionState,
  now: number
): AttributionFields {
  const latest =
    state.latest &&
    typeof state.latest.seenAt === "number" &&
    now - state.latest.seenAt <= LATEST_VISIT_MAX_AGE_MS &&
    now >= state.latest.seenAt
      ? state.latest
      : {};
  return normalizeAttribution({ ...latest, ...state.first });
}
